import _ from 'lodash';
import { ToolCallNodeDataSchema } from '@cotera/api';
import { collectTemplateRefs } from '@cotera/utilities';
import type { WorkflowNodeData } from '../types';
import type {
  WorkflowNodeHydration,
  WorkflowNodeUpstreamRef,
} from '../workflow-node-base';
import type { WorkflowNodeOutputShapeContext } from '../workflow-node-output-shape';
import { nonBlank } from './output-shape-helpers';
import { TypedWorkflowNode } from './typed-workflow-node';

const { startCase } = _;

export class ToolCallWorkflowNode extends TypedWorkflowNode<
  typeof ToolCallNodeDataSchema
> {
  protected readonly schema = ToolCallNodeDataSchema;

  static override create(
    data: WorkflowNodeData,
    hydration: WorkflowNodeHydration
  ): ToolCallWorkflowNode {
    return new ToolCallWorkflowNode(data, hydration);
  }
  override deriveTypedOutputShape(
    context: WorkflowNodeOutputShapeContext
  ): string | null {
    const toolKey = nonBlank(this.config?.key);
    if (toolKey === undefined) {
      return null;
    }
    return context.resolveToolOutputShape(toolKey);
  }

  override upstreamRefs(): readonly WorkflowNodeUpstreamRef[] {
    const config = this.config;
    if (config === null) {
      return [];
    }
    // `{{ref}}` tokens inside params resolve through the same code as the
    // bindings, so they're refs too — where a dataset write keeps all of its.
    const paramTemplateRefs = collectTemplateRefs(config.params).map(
      ({ path, ref }) => ({
        paramKey: path === '' ? 'params' : `params.${path}`,
        ref,
        template: true,
      })
    );
    return [...this.bindingMapRefs(config.paramBindings), ...paramTemplateRefs];
  }

  protected override defaultDisplayName(): string {
    const lastSegment = nonBlank(this.config?.key)
      ?.split('/')
      .filter((segment) => segment.length > 0)
      .pop();
    return lastSegment === undefined ? 'Tool call' : startCase(lastSegment);
  }
}
